/**
 * Carry an old-platform password over, once (POS2-1309).
 *
 * One copy, used by both screens where somebody types a password they already have: the login page
 * and the invitation page's sign-in form. The second is the one a carried-over user actually
 * reaches, because it is where our own invitation email sends them.
 *
 * `migrated: true` means the backend verified the old Django hash and has just set it as this
 * account's Supabase password — creating the account first if a pending invitation for the address
 * allowed it. The caller then retries the ordinary `signInWithPassword`, which is the sign-in the
 * user thinks they are already doing.
 *
 * **Everything else is `false`, deliberately including the errors.** The endpoint answers a
 * byte-identical 401 for an unknown address, a spent row, a wrong password and an uninvited
 * address, because each of those is a fact about whether somebody was carried over. This function
 * keeps that property by collapsing every outcome into one boolean: there is nothing here for a
 * caller to branch on except "it worked", so no call site can turn it back into a directory of who
 * was carried over.
 */
const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

export async function tryLegacyPassword(email: string, password: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_URL}/api/v1/auth/legacy-login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!response.ok) return false;
    const body = await response.json();
    return body.migrated === true;
  } catch {
    // An extra chance, never a new way to fail: a network error here has to leave the caller with
    // the sign-in error it already had.
    return false;
  }
}
